import React, { useState, useEffect } from "react";
import AdminDashboard from "../Dashboard/Dashboard";
import axios from "axios";
import { BASE_URL } from "../../Api/ApiUrls";

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from "@mui/material";

const AdminRequests = () => {
  const [consultantReqs, setConsultantReqs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("interior"); // Set default to interior

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [consultantRes, usersRes] = await Promise.all([
        axios.get(`${BASE_URL}/consultant-req/`),
        axios.get(`${BASE_URL}/users/`),
      ]);

      setConsultantReqs(consultantRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError("Failed to fetch data. Please check backend server.");
    } finally {
      setLoading(false);
    }
  };

  const getUserDetails = (userId) => {
    const user = users.find(
      (user) => user.id === userId || user.user_id === userId
    );
    if (!user) return { name: "Unknown", mobile_no: "N/A" };

    return {
      name: user.name || user.username || "Unknown",
      mobile_no: user.mobile_no || user.phone || "N/A",
    };
  };

  // Remove duplicates - only show one entry per user per interest
  const getUniqueRequests = () => {
    const uniqueMap = new Map();
    
    consultantReqs.forEach(req => {
      const key = `${req.user_id}-${req.interested_on}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, req);
      }
    });
    
    return Array.from(uniqueMap.values());
  };

  // Apply filter on unique requests - FIXED: Only show Interior or Construction
  const filteredRequests = getUniqueRequests().filter((req) => {
    if (filter === "interior") return req.interested_on === "Interior";
    if (filter === "construction") return req.interested_on === "Construction";
    return false; // Don't show anything when no filter is selected
  });

  if (loading) {
    return (
      <>
        <AdminDashboard />
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" flexDirection="column">
          <CircularProgress />
          <Typography mt={2}>Loading Requests...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminDashboard />
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={fetchData}>
            Retry
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <AdminDashboard />
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 1,
          borderRadius: 2,
          boxShadow: 3,
          marginTop: 10,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Request Table
        </Typography>
        <Box p={0.2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
           
            <Box>
              <Button
                variant={filter === "interior" ? "contained" : "outlined"}
                color="primary"
                onClick={() => setFilter("interior")}
                sx={{ mr: 1 }}
              >
                Interior
              </Button>
              <Button
                variant={filter === "construction" ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setFilter("construction")}
              >
                Construction
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                  <TableCell sx={{ color: "#fff" }}>S No</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Mobile</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Interest</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No {filter} requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((req) => {
                    const userDetails = getUserDetails(req.user_id);
                    return (
                      <TableRow key={`${req.user_id}-${req.interested_on}`}>
                     <TableCell>{String(req.user_id).padStart(4, "0")}</TableCell>

                        <TableCell>{userDetails.name}</TableCell>
                        <TableCell>{userDetails.mobile_no}</TableCell>
                        <TableCell>
                          {req.interested_on ? (
                            <Chip
                              label={req.interested_on}
                              color={req.interested_on === "Interior" ? "primary" : "secondary"}
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">N/A</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default AdminRequests;